import type { ReactElement } from 'react';
import type { ReferralListRow, ReferralStatus } from '@xdocs/schemas';
import { Badge, DataTable, cn } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { optionalProp } from '../../lib/optionalProp.js';
import { useReferralList } from './useReferralList.js';
import { useSharesT } from './useSharesT.js';
import styles from './ReferralPanel.module.css';

const STATUS_TONE: Record<ReferralStatus, BadgeTone> = {
  pending: 'neutral',
  converted: 'info',
  credited: 'success',
  void: 'error',
};

/** F72 referral status list (design/app/35-shares.html Referrals tab):
 * referred person, document, status, credits earned (derived from the
 * ledger at read time — never a second stored total, F72/F73 AC). */
export function ReferralPanel(): ReactElement {
  const { t } = useSharesT();
  const { rows, total, nextCursor, query, onQueryChange, onLoadMore, isLoading, isLoadingMore, isError, loadMoreError, onRetry } = useReferralList();

  const columns: ColumnDef<ReferralListRow>[] = [
    {
      id: 'referred',
      header: t('shares:referral.column.referred'),
      primary: true,
      cell: (row) => <span className={cn(styles['cell'])}>{row.referredDisplayName ?? '—'}</span>,
    },
    {
      id: 'document',
      header: t('shares:referral.column.document'),
      cell: (row) => <span className={cn(styles['cell'])}>{row.bookTitle ?? '—'}</span>,
    },
    {
      id: 'status',
      header: t('shares:referral.column.status'),
      cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`shares:referral.status.${row.status}`)}</Badge>,
    },
    {
      id: 'earned',
      header: t('shares:referral.column.earned'),
      cell: (row) => <span className={cn(styles['earned'])}>{t('shares:referral.earned', { count: row.creditsEarned })}</span>,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      query={query}
      onQueryChange={onQueryChange}
      label={t('shares:tabs.referrals')}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      total={total}
      nextCursor={nextCursor}
      onLoadMore={onLoadMore}
      isLoadingMore={isLoadingMore}
      {...optionalProp('loadMoreError', loadMoreError)}
      emptyTitle={t('shares:referral.empty.title')}
      emptyDescription={t('shares:referral.empty.description')}
      noResultsTitle={t('shares:list.noResults.title', { query: query.q ?? '' })}
      onClearFilters={() => {
        const { q: _q, ...rest } = query;
        onQueryChange({ ...rest, page: 1 });
      }}
    />
  );
}
